import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { MatIconModule } from "@angular/material/icon";
import { MatCardModule } from "@angular/material/card";
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatButtonModule } from "@angular/material/button";
import { AsyncPipe, DOCUMENT, NgClass, NgIf } from "@angular/common";
import { HttpClient } from "@angular/common/http";
import { ActivatedRoute, Router, RouterLink, NavigationStart } from "@angular/router";
import { MatBottomSheet } from "@angular/material/bottom-sheet";
import { Title } from "@angular/platform-browser";
import { hymnsDetailed } from "../hymn/hymnsDetailed";
import { MidiPlayerService } from "../service/midi-player.service";
import { LayoutService } from "../service/layout.service";
import { ShareService } from "../service/share.service";
import { HymnSelectionService } from "../service/hymn-selection.service";
import { MatMenuModule } from "@angular/material/menu";
import { SidenavService } from "../service/sidenav.service";
import { FontSizeComponent } from "../controls/font-size/font-size.component";
import { SettingsService } from "../service/settings.service";
import { MusicControlComponent } from "../controls/music-control/music-control.component";
import { ModalService } from "../service/modal.service";
import { Hymn } from "../hymn/hymn";
import { translation } from "../translation";
import { DoubleTapDirective } from "../directive/double-tap.directive";
import { PdfViewerModule, PdfViewerComponent } from 'ng2-pdf-viewer';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { ThemeService } from '../service/theme.service';
import { SwipeGestureDirective } from "../directive/swipe-gesture.directive";

@Component({
  selector: 'app-sheet-music',
  standalone: true,
  imports: [
    MatIconModule,
    MatCardModule,
    MatToolbarModule,
    MatButtonModule,
    MatMenuModule,
    MatProgressBarModule,
    RouterLink,
    NgIf,
    MusicControlComponent,
    PdfViewerModule,
    NgClass,
    DoubleTapDirective,
  ],
  templateUrl: './sheet-music.component.html',
  styleUrl: './sheet-music.component.scss'
})
export class SheetMusicComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild('headerChorus') public headerChorus!: ElementRef;
  @ViewChild('headerContent') public headerContent!: ElementRef;
  @ViewChild('chorus') public chorus!: ElementRef;
  @ViewChild('content_container') public content!: ElementRef;
  @ViewChild('scrollWrapper') public scrollWrapper!: ElementRef;
  @ViewChild('pdfContainer') public pdfContainer!: ElementRef<HTMLDivElement>;
  @ViewChild(PdfViewerComponent) public pdfComponent?: PdfViewerComponent;

  hymn: any = null;
  pdfSrc: string = '';
  zoom: number = this.settingService.getSheetMusicZoom() || 1;
  pdfError: boolean = false;
  pdfLoading: boolean = false;
  pdfThemeMode: 'white' | 'day' | 'night' | 'black' = 'day';
  elem: any;
  fontSizeBase = this.settingService.getReadModeFontSize();
  fontSize = this.fontSizeBase;
  fontSizeStyle = this.calculateFontSizeStyle();
  fontSizeMax = 100;
  fontSizeMin = 8;
  lineHeightMultiplier = 1.5;
  lineHeightStyle = this.calculateLineHeightStyle();
  lastHymnNumber = hymnsDetailed[hymnsDetailed.length - 1].hymnNumber;

  readFontFamily: string | undefined = this.settingService.getFontFamily();
  readFontWeight: number | undefined = this.settingService.getFontWeight();

  // Clef navigation support
  private readonly gClef: string = "\uE050"; // SMuFL G clef
  private gClefMatches: HTMLElement[] = [];
  private gClefIndex: number = -1;
  private textLayerObserver?: MutationObserver;
  private highlightOverlay?: HTMLDivElement;
  public clefUiEnabled: boolean = false;
  private resizeObserver?: ResizeObserver;

  // Native Pinch Zoom State
  private initialDistance = 0;
  private currentPinchScale = 1;
  private initialMidpoint = { x: 0, y: 0 };
  private pinchViewerElement?: HTMLElement | null;

  // Bound listeners for precise cleanup
  private touchStartListener = this.onTouchStart.bind(this);
  private touchMoveListener = this.onTouchMove.bind(this);
  private touchEndListener = this.onTouchEnd.bind(this);

  constructor(
    @Inject(DOCUMENT) private document: any,
    private http: HttpClient,
    private route: ActivatedRoute,
    private ngZone: NgZone,
    private bottomSheet: MatBottomSheet,
    private titleService: Title,
    public router: Router,
    public midiService: MidiPlayerService,
    public hymnSelectionService: HymnSelectionService,
    public shareService: ShareService,
    public sidenavService: SidenavService,
    protected settingService: SettingsService,
    protected modalService: ModalService,
    protected layoutService: LayoutService,
    private themeService: ThemeService) {

    settingService.readModeFontSizeSubject.subscribe(() => {
      this.ngZone.run(() => {
        this.fontSizeBase = this.settingService.getReadModeFontSize();
        this.fontSize = this.settingService.getReadModeFontSize();
        this.fontSizeStyle = this.calculateFontSizeStyle();
        this.lineHeightStyle = this.calculateLineHeightStyle();
      });
    });

    settingService.fontFamilySubject.subscribe((f) => {
      this.ngZone.run(() => { this.readFontFamily = f; });
    });

    settingService.fontWeightSubject.subscribe((w) => {
      this.ngZone.run(() => { this.readFontWeight = (w ?? 400); });
    });
  }

  ngAfterViewInit(): void {
    const sw = this.scrollWrapper?.nativeElement as HTMLElement | undefined;
    if (sw && (!sw.style.touchAction || sw.style.touchAction === 'auto')) {
      sw.style.touchAction = 'pan-y';
    }

    // Attach native touch listeners specifically to the scrolling container
    const container = this.pdfContainer?.nativeElement;
    if (container) {
      container.addEventListener('touchstart', this.touchStartListener, { passive: false });
      container.addEventListener('touchmove', this.touchMoveListener, { passive: false });
      container.addEventListener('touchend', this.touchEndListener);
      container.addEventListener('touchcancel', this.touchEndListener);
    }
  }

  ngOnDestroy(): void {
    this.midiService.stop();
    try { this.textLayerObserver?.disconnect(); } catch {}
    try { this.resizeObserver?.disconnect(); } catch {}

    const container = this.pdfContainer?.nativeElement;
    if (container) {
      container.removeEventListener('touchstart', this.touchStartListener);
      container.removeEventListener('touchmove', this.touchMoveListener);
      container.removeEventListener('touchend', this.touchEndListener);
      container.removeEventListener('touchcancel', this.touchEndListener);
    }
  }

  // --- NATIVE PINCH ZOOM LOGIC ---

  private getDistance(t1: Touch, t2: Touch): number {
    return Math.sqrt(Math.pow(t2.clientX - t1.clientX, 2) + Math.pow(t2.clientY - t1.clientY, 2));
  }

  private getMidpoint(t1: Touch, t2: Touch): { x: number, y: number } {
    return {
      x: (t1.clientX + t2.clientX) / 2,
      y: (t1.clientY + t2.clientY) / 2
    };
  }

  private onTouchStart(event: TouchEvent) {
    if (event.touches.length !== 2) return;

    // Grab the internal wrapper that ng2-pdf-viewer generates so we can transform it
    this.pinchViewerElement = this.pdfContainer.nativeElement.querySelector('.ng2-pdf-viewer-container') as HTMLElement;
    if (!this.pinchViewerElement) return;

    this.initialDistance = this.getDistance(event.touches[0], event.touches[1]);
    this.initialMidpoint = this.getMidpoint(event.touches[0], event.touches[1]);
    this.currentPinchScale = 1;

    // Set the transform origin perfectly to the pinch midpoint, factoring in scroll state
    const rect = this.pdfContainer.nativeElement.getBoundingClientRect();
    const originX = this.initialMidpoint.x - rect.left + this.pdfContainer.nativeElement.scrollLeft;
    const originY = this.initialMidpoint.y - rect.top + this.pdfContainer.nativeElement.scrollTop;

    this.pinchViewerElement.style.transformOrigin = `${originX}px ${originY}px`;
    this.pinchViewerElement.style.transition = 'none'; // Snappy scaling
  }

  private onTouchMove(event: TouchEvent) {
    if (this.initialDistance <= 0 || event.touches.length !== 2 || !this.pinchViewerElement) return;

    // Stop the browser from attempting a native page zoom or scroll
    event.preventDefault();

    const currentDistance = this.getDistance(event.touches[0], event.touches[1]);
    this.currentPinchScale = currentDistance / this.initialDistance;

    // Clamp the temporary scale so it doesn't get wildly large or small
    const maxAllowedScale = 5.0 / this.zoom;
    const minAllowedScale = 0.25 / this.zoom;
    this.currentPinchScale = Math.max(minAllowedScale, Math.min(this.currentPinchScale, maxAllowedScale));

    // Apply the smooth CSS transform
    this.pinchViewerElement.style.transform = `scale(${this.currentPinchScale})`;
  }

  private onTouchEnd(event: TouchEvent) {
    if (this.initialDistance <= 0 || !this.pinchViewerElement) return;

    // If it was just a tiny micro-movement, simply reset and abort
    if (Math.abs(this.currentPinchScale - 1) < 0.02) {
      this.pinchViewerElement.style.transform = 'none';
      this.initialDistance = 0;
      return;
    }

    this.ngZone.run(() => {
      const container = this.pdfContainer.nativeElement;
      const rect = container.getBoundingClientRect();

      // 1. Math: How much the viewport needs to scroll to stay perfectly aligned
      const midXRelativeToContainer = this.initialMidpoint.x - rect.left;
      const midYRelativeToContainer = this.initialMidpoint.y - rect.top;

      const scrollAdjustmentX = (midXRelativeToContainer + container.scrollLeft) * (this.currentPinchScale - 1);
      const scrollAdjustmentY = (midYRelativeToContainer + container.scrollTop) * (this.currentPinchScale - 1);

      // 2. Wipe the CSS transform instantly
      this.pinchViewerElement!.style.transform = 'none';
      this.pinchViewerElement!.style.transformOrigin = 'center center';

      // 3. Bake the scale into the native PDF engine for crispness
      let newZoom = this.zoom * this.currentPinchScale;
      this.zoom = Math.max(0.25, Math.min(newZoom, 5));

      const viewer: any = (this.pdfComponent as any)?.pdfViewer;
      if (viewer) {
        viewer.currentScaleValue = this.zoom;
      }

      // 4. Apply the calculated scroll adjustment right after the DOM natively resizes
      setTimeout(() => {
        container.scrollLeft += scrollAdjustmentX;
        container.scrollTop += scrollAdjustmentY;
      }, 10);

      // 5. Cleanup, save state, and trigger the highlight UI calculations
      this.initialDistance = 0;
      this.settingService.setSheetMusicZoom(this.zoom);
      this.updateScrollableStateDebounced();
      setTimeout(() => this.indexGClefSpans(), 200);
    });
  }

  // --- KEYBOARD & LAYOUT LISTENER LOGIC ---

  @HostListener('window:resize')
  onWindowResize() {
    this.updateScrollableStateDebounced();
  }

  @HostListener('window:keyup', ['$event'])
  keyEvent(event: KeyboardEvent) {
    this.ngZone.run(() => {
      if (!this.hymnSelectionService.searchFocus && !this.hymnSelectionService.dialpadActive) {
        switch (event.key) {
          case 'ArrowLeft':
          case 'b':
            this.onSwipePrevious();
            break;
          case 'ArrowRight':
          case 'n':
            this.onSwipeNext();
            break;
          case 'ArrowUp':
            if (this.clefUiEnabled) { event.preventDefault(); this.jumpGlyph(-1); }
            break;
          case 'ArrowDown':
            if (this.clefUiEnabled) { event.preventDefault(); this.jumpGlyph(1); }
            break;
          case 'd':
          case 'D':
            this.hymnSelectionService.dialpad();
            break;
          case 'l':
          case 'L':
            this.sidenavService.toggleList();
            break;
          case 'w':
          case 'W':
            this.hymnSelectionService.goToRandomHymn(false);
            break;
          case 'f':
          case 'F':
            this.layoutService.fullscreen ?
              this.layoutService.exitFullScreen() : this.layoutService.enterFullScreen();
            break;
          case 'g':
          case 'G':
            this.hymnSelectionService.goToRandomHymn(true);
            break;
          case 'v':
          case 'V':
            this.router.navigate([`/${translation.path_present}/`+this.hymn.hymnNumber]);
            break;
          case '+':
            // In Sheet Music view, +/- should control PDF zoom, not text size
            this.zoomIn();
            break;
          case '-':
            this.zoomOut();
            break;
          case '<':
          case ',':
            this.midiService.decreasePlaybackSpeed();
            break;
          case '>':
          case '.':
            this.midiService.increasePlaybackSpeed();
            break;
          case 'm':
            this.midiService.setPlaybackSpeed(1.0);
            break;
          case 'i':
            if(!this.modalService.active) {
              this.modalService.openDialog('shortcuts')
            } else {
              this.modalService.closeDialog();
            }
            break;
          case 'Escape':
            if(this.modalService.active) {
              this.modalService.closeDialog();
            }
            break;
        }
      } else {
        switch (event.key) {
          case 'd':
          case 'D':
            this.hymnSelectionService.dialpadCancel();
            break;
          case 'Escape':
            this.hymnSelectionService.dialpadCancel();
            this.hymnSelectionService.searchFocus = false;
            break;
        }
      }
    });
  }

  calculateFontSizeStyle() {
    return this.fontSize + 'px';
  }

  calculateLineHeightStyle() {
    return this.fontSize * this.lineHeightMultiplier + 'px';
  }

  toggleListSidenav() {
    this.sidenavService.toggleList();
  }

  toggleSettingSidenav() {
    this.settingService.toggleSettings();
  }

  ngOnInit() {
    this.settingService.setSheetMusicViewMode();
    this.pdfThemeMode = this.settingService.getSheetMusicPdfThemeMode();
    const storedZoom = this.settingService.getSheetMusicZoom();
    if (storedZoom && typeof storedZoom === 'number') {
      this.zoom = storedZoom;
    }
    this.elem = document.documentElement;

    this.router.events.subscribe(ev => {
      if (ev instanceof NavigationStart) {
        this.bottomSheet.dismiss();
      }
    });

    this.route.paramMap.subscribe(params => {
      if (params.get('reference')) {
        setTimeout(() => {
          if(this.layoutService.singleView) {
            this.sidenavService.closeList();
          }
        }, 10);
        this.getJSON(params.get('reference')!);
        this.hymnSelectionService.selectedHymn = this.hymn;
        this.settingService.addToHistory(this.hymn.hymnNumber);
      } else {
        this.hymn = this.settingService.getLastHymn();
        this.hymnSelectionService.selectedHymn = this.hymn;
      }
      this.titleService.setTitle(`${translation.hymn} ${this.hymn.hymnNumber} - ${this.hymn.title} - ${translation.title_full}`);
      this.updatePdfSrc();
      this.midiService.stop();
    });
  }

  public getJSON(reference: string) {
    this.hymn = hymnsDetailed[+reference - 1];
  }

  updatePdfSrc() {
    this.gClefIndex = -1;
    if (this.highlightOverlay && this.highlightOverlay.parentNode) {
      this.highlightOverlay.parentNode.removeChild(this.highlightOverlay);
      this.highlightOverlay = undefined;
    }

    this.clefUiEnabled = false;

    if (this.hymn && this.hymn.hymnNumber) {
      this.pdfError = false;
      this.pdfLoading = true;
      this.pdfSrc = `../assets/sheet-music/${this.hymn.hymnNumber}.pdf`;
    } else {
      this.pdfSrc = '';
      this.pdfLoading = false;
    }
  }

  zoomIn() {
    this.zoom = Math.min(this.zoom + 0.15, 5);
    const viewer: any = (this.pdfComponent as any)?.pdfViewer;
    if (viewer) {
      viewer.currentScaleValue = this.zoom;
      this.zoom = viewer.currentScale || this.zoom;
    }
    this.settingService.setSheetMusicZoom(this.zoom);
    this.updateScrollableStateDebounced();
    setTimeout(() => this.indexGClefSpans(), 200);
  }

  zoomOut() {
    this.zoom = Math.max(this.zoom - 0.15, 0.25);
    const viewer: any = (this.pdfComponent as any)?.pdfViewer;
    if (viewer) {
      viewer.currentScaleValue = this.zoom;
      this.zoom = viewer.currentScale || this.zoom;
    }
    this.settingService.setSheetMusicZoom(this.zoom);
    this.updateScrollableStateDebounced();
    setTimeout(() => this.indexGClefSpans(), 200);
  }

  resetZoom() {
    const viewer: any = (this.pdfComponent as any)?.pdfViewer;
    if (viewer) {
      viewer.currentScaleValue = 'page-width';
    }

    this.zoom = 1.0;
    this.settingService.setSheetMusicZoom(this.zoom);
    this.updateScrollableStateDebounced();
    setTimeout(() => this.indexGClefSpans(), 200);
  }

  async onPdfLoaded(pdf: any) {
    this.pdfLoading = false;
    const viewer: any = (this.pdfComponent as any)?.pdfViewer;

    const storedZoom = this.settingService.getSheetMusicZoom();
    if (viewer) {
      if (storedZoom && typeof storedZoom === 'number') {
        this.zoom = storedZoom;
      } else {
        this.zoom = 1.0;
      }
    }

    const touchLayout = () => {
      const scrollEl = document.querySelector('.mat-sidenav-content') as HTMLElement | null;
      if (scrollEl) {
        void scrollEl.offsetHeight;
      } else {
        void (document.documentElement as HTMLElement).offsetHeight;
      }
      document.documentElement.style.setProperty('--layout-touch-seq', String(Date.now() % 1000));
    };
    Promise.resolve().then(touchLayout);
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => touchLayout());
    }

    try { this.resizeObserver?.disconnect(); } catch {}
    const container = this.getPdfContainerEl();
    if (container) {
      this.resizeObserver = new ResizeObserver(() => this.updateScrollableStateDebounced());
      this.resizeObserver.observe(container);
    }

    setTimeout(() => this.updateScrollableState(), 250);
  }

  onPdfError(e: any) {
    this.pdfError = true;
    this.pdfLoading = false;
    this.gClefMatches = [];
    this.gClefIndex = -1;
    this.textLayerObserver?.disconnect();
    this.clefUiEnabled = false;
    if (this.highlightOverlay && this.highlightOverlay.parentNode) {
      this.highlightOverlay.parentNode.removeChild(this.highlightOverlay);
      this.highlightOverlay = undefined;
    }
  }

  private getPdfContainerEl(): HTMLElement | null {
    const viewer: any = (this.pdfComponent as any)?.pdfViewer;
    return (viewer?.container as HTMLElement) || (this.pdfContainer?.nativeElement as HTMLElement) || null;
  }

  private updateScrollableStateDebounced: () => void = (() => {
    let t: any;
    return () => {
      clearTimeout(t);
      t = setTimeout(() => this.updateScrollableState(), 120);
    };
  })();

  private updateScrollableState() {
    const container = this.getPdfContainerEl();
    const isNativeScrollable = !!(container && (container.scrollHeight - container.clientHeight) > 1);
    const isZoomed = this.zoom > 1.05;

    // Enable the Clef UI if the native container overflows or is zoomed in
    const next = isNativeScrollable || isZoomed;

    if (next === this.clefUiEnabled) return;

    this.ngZone.run(() => {
      this.clefUiEnabled = next;

      if (!this.clefUiEnabled) {
        this.textLayerObserver?.disconnect();
        this.gClefMatches = [];
        this.gClefIndex = -1;
        if (this.highlightOverlay && this.highlightOverlay.parentNode) {
          this.highlightOverlay.parentNode.removeChild(this.highlightOverlay);
          this.highlightOverlay = undefined;
        }
      } else {
        this.watchTextLayerChanges();
        setTimeout(() => this.indexGClefSpans(), 150);
      }
    });
  }

  private renderHighlight() {
    if (!this.clefUiEnabled) return;
    if (this.gClefIndex < 0 || this.gClefIndex >= this.gClefMatches.length) return;

    const el = this.gClefMatches[this.gClefIndex];
    if (!document.body.contains(el)) return;

    const pageContainer = el.closest('.page') as HTMLElement;
    if (!pageContainer) return;

    if (!this.highlightOverlay) {
      this.highlightOverlay = document.createElement('div');
      this.highlightOverlay.classList.add('custom-highlight-overlay');
      this.highlightOverlay.style.position = 'absolute';
      this.highlightOverlay.style.backgroundColor = 'rgba(255, 215, 0, 0.4)';
      this.highlightOverlay.style.borderRadius = '50%';
      this.highlightOverlay.style.pointerEvents = 'none';
      this.highlightOverlay.style.zIndex = '100';
      this.highlightOverlay.style.transition = 'all 0.2s ease-in-out';
    }

    if (this.highlightOverlay.parentElement !== pageContainer) {
      pageContainer.appendChild(this.highlightOverlay);
    }

    const pageRect = pageContainer.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();

    let currentScale = 1;
    if (pageContainer.offsetWidth > 0) {
      currentScale = pageRect.width / pageContainer.offsetWidth;
    }

    const top = (elRect.top - pageRect.top) / currentScale;
    const left = (elRect.left - pageRect.left) / currentScale;
    const width = elRect.width / currentScale;
    const height = elRect.height / currentScale;

    const padding = 8;
    const size = Math.max(width, height) + (padding * 2);

    const centerX = left + (width / 2);
    const centerY = top + (height / 2);

    this.highlightOverlay.style.top = `${centerY - (size / 2)}px`;
    this.highlightOverlay.style.left = `${centerX - (size / 2)}px`;
    this.highlightOverlay.style.width = `${size}px`;
    this.highlightOverlay.style.height = `${size}px`;

    try { el.focus({ preventScroll: true }); } catch {}
  }

  private indexGClefSpans() {
    if (!this.clefUiEnabled) return;
    if (this.highlightOverlay && this.highlightOverlay.parentNode) {
      this.highlightOverlay.parentNode.removeChild(this.highlightOverlay);
    }

    const container = this.getPdfContainerEl();
    if (!container) { this.gClefMatches = []; this.gClefIndex = -1; return; }

    const spans = container.querySelectorAll('.textLayer span');
    const matches: { el: HTMLElement, page: number, y: number, x: number }[] = [];

    for (const s of Array.from(spans)) {
      if (!(s as HTMLElement).textContent?.includes(this.gClef)) continue;
      const el = s as HTMLElement;

      const pageDiv = el.closest('.page') as HTMLElement | null;
      const pageAttr = pageDiv?.getAttribute('data-page-number');
      const page = pageAttr ? parseInt(pageAttr, 10) : Number.MAX_SAFE_INTEGER;

      let y = 0, x = 0;
      try {
        if (pageDiv) {
          const elRect = el.getBoundingClientRect();
          const pageRect = pageDiv.getBoundingClientRect();
          y = elRect.top - pageRect.top;
          x = elRect.left - pageRect.left;
        } else {
          const r = el.getBoundingClientRect();
          y = r.top;
          x = r.left;
        }
      } catch {}

      matches.push({ el, page, y, x });
    }

    matches.sort((a, b) => (a.page - b.page) || (a.y - b.y) || (a.x - b.x));
    this.gClefMatches = matches.map(m => m.el);

    if (this.gClefIndex === -1 && this.gClefMatches.length > 0) {
      this.gClefIndex = 0;
    }

    if (this.gClefIndex >= this.gClefMatches.length) {
      this.gClefIndex = this.gClefMatches.length > 0 ? 0 : -1;
    }

    if (this.gClefIndex !== -1) {
      this.renderHighlight();
    }
  }

  private watchTextLayerChanges() {
    const container = this.getPdfContainerEl();
    if (!container) return;
    this.textLayerObserver?.disconnect();
    let debounceId: any;

    this.textLayerObserver = new MutationObserver((mutations) => {
      let hasRelevantChanges = false;

      for (const m of mutations) {
        if ((m.target as HTMLElement).classList?.contains('custom-highlight-overlay')) continue;

        if (m.type === 'characterData') {
          hasRelevantChanges = true;
          break;
        }

        for (let i = 0; i < m.addedNodes.length; i++) {
          const node = m.addedNodes[i] as HTMLElement;
          if (node.nodeType === 1 && node.classList?.contains('custom-highlight-overlay')) continue;
          hasRelevantChanges = true;
        }

        for (let i = 0; i < m.removedNodes.length; i++) {
          const node = m.removedNodes[i] as HTMLElement;
          if (node.nodeType === 1 && node.classList?.contains('custom-highlight-overlay')) continue;
          hasRelevantChanges = true;
        }

        if (hasRelevantChanges) break;
      }

      if (!hasRelevantChanges) return;

      clearTimeout(debounceId);
      debounceId = setTimeout(() => this.indexGClefSpans(), 120);
    });

    this.textLayerObserver.observe(container, { subtree: true, childList: true, characterData: true });
  }

  private scrollClefToTop(el: HTMLElement) {
    const container = this.getPdfContainerEl();
    if (!container) return;

    try {
      const cRect = container.getBoundingClientRect();
      const eRect = el.getBoundingClientRect();

      const relativeTop = eRect.top - cRect.top;

      const padding = 20;
      let targetTop = container.scrollTop + relativeTop - padding;

      const maxTop = container.scrollHeight - container.clientHeight;
      if (targetTop < 0) targetTop = 0;
      if (targetTop > maxTop) targetTop = maxTop;

      container.scrollTo({ top: targetTop, behavior: "smooth" });
    } catch {
      setTimeout(() => this.scrollClefToTop(el), 50);
    }
  }

  jumpGlyph(dir: 1 | -1) {
    if (!this.clefUiEnabled) return;

    if (!this.gClefMatches.length) this.indexGClefSpans();
    if (!this.gClefMatches.length) return;

    this.gClefIndex = (this.gClefIndex + dir + this.gClefMatches.length) % this.gClefMatches.length;
    let el = this.gClefMatches[this.gClefIndex];

    if (!document.body.contains(el)) {
      this.indexGClefSpans();
      if (!this.gClefMatches.length) return;
      this.gClefIndex = 0;
      el = this.gClefMatches[this.gClefIndex];
    }

    this.scrollClefToTop(el);

    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => requestAnimationFrame(() => this.renderHighlight()));
    } else {
      setTimeout(() => this.renderHighlight(), 50);
    }
  }

  pdfThemeClass() {
    return {
      'pdf-white': this.pdfThemeMode === 'white',
      'pdf-day': this.pdfThemeMode === 'day',
      'pdf-night': this.pdfThemeMode === 'night',
      'pdf-black': this.pdfThemeMode === 'black',
    };
  }

  setPdfThemeMode(mode: 'white' | 'day' | 'night' | 'black') {
    this.pdfThemeMode = mode;
    this.settingService.setSheetMusicPdfThemeMode(mode);
  }

  onSwipePrevious() {
    if (this.hymn && this.hymn.hymnNumber > 1) {
      this.ngZone.run(() => this.router.navigate([`${translation.path_sheet_music}/` + (this.hymn.hymnNumber - 1)])).then();
    }
  }

  onSwipeNext() {
    if (this.hymn && this.hymn.hymnNumber < this.lastHymnNumber) {
      this.ngZone.run(() => this.router.navigate([`${translation.path_sheet_music}/` + (this.hymn.hymnNumber + 1)])).then();
    }
  }

  changeFontSize() {
    this.layoutService.fontSizeSheetActive = true;

    const bottomSheetRef = this.bottomSheet.open(FontSizeComponent, {
      data: {
        fontSizeEnabled: true,
        linesPerVerseEnabled: false,
        fontSizeMin: this.fontSizeMin,
        fontSizeMax: this.fontSizeMax,
        currentFontSize: this.fontSize,
        fontSizeChange: new EventEmitter<number>(),
        fonts: this.settingService.getAvailableFonts(),
        currentFontFamily: this.settingService.getFontFamily(),
        fontFamilyChange: new EventEmitter<string | undefined>(),
        fontWeightEnabled: true,
        currentFontWeight: this.settingService.getFontWeight(),
        fontWeightChange: new EventEmitter<number | undefined>(),
        textOutlineToggleEnabled: true,
        textOutlineEnabled: this.settingService.getTextOutlineEnabled(),
        textOutlineChange: new EventEmitter<boolean>(),
        textAlignmentEnabled: true,
        currentTextAlignment: this.settingService.getTextAlignmentReadMode(),
        textAlignmentChange: new EventEmitter<string>()
      },
    });

    bottomSheetRef.instance.data.fontSizeChange?.subscribe((newFontSize: number) => {
      this.updateFontSize(newFontSize);
    });

    bottomSheetRef.instance.data.fontFamilyChange?.subscribe((fam: string | undefined) => {
      this.settingService.setFontFamily(fam);
    });

    bottomSheetRef.instance.data.fontWeightChange?.subscribe((w: number | undefined) => {
      this.settingService.setFontWeight(w);
    });

    bottomSheetRef.instance.data.textOutlineChange?.subscribe((enabled: boolean) => {
      this.settingService.setTextOutlineEnabled(enabled);
    });

    bottomSheetRef.instance.data.textAlignmentChange?.subscribe((al: string) => {
      this.settingService.setTextAlignmentReadMode(al);
    });

    bottomSheetRef.afterDismissed().subscribe(() => {
      this.layoutService.fontSizeSheetActive = false;
    });
  }

  updateFontSize(fontSize: number) {
    this.fontSizeBase = fontSize;
    this.settingService.setFontSize(fontSize);
    this.fontSize = fontSize;
    this.fontSizeStyle = this.calculateFontSizeStyle();
    this.lineHeightStyle = this.calculateLineHeightStyle();
  }

  protected readonly Hymn = Hymn;
  protected readonly translation = translation;
  protected readonly hymnsDetailed = hymnsDetailed;

  async onDoubleTap() {
    if(this.layoutService.fullscreenSupported()) {
      await this.layoutService.toggleFullScreen();
    }
  }
}
